export function quickBooksTimeNeedsReconnect(status: {
  clientConfigured?: boolean;
  connected?: boolean;
  tokenHealthy?: boolean;
  needsReconnect?: boolean;
} | null | undefined): boolean {
  if (status == null || status.clientConfigured !== true) {
    return false;
  }
  if (status.needsReconnect === true) {
    return true;
  }
  if (status.connected === true && status.tokenHealthy === false) {
    return true;
  }
  return status.connected !== true;
}

export function quickBooksTimeNeedsReconnectSoon(status: {
  clientConfigured?: boolean;
  connected?: boolean;
  tokenHealthy?: boolean;
  needsReconnect?: boolean;
  needsReconnectSoon?: boolean;
} | null | undefined): boolean {
  if (status == null || status.clientConfigured !== true) {
    return false;
  }
  if (quickBooksTimeNeedsReconnect(status)) {
    return false;
  }
  return status.needsReconnectSoon === true;
}

export function quickBooksTimeOAuthBannerVisible(status: {
  clientConfigured?: boolean;
  connected?: boolean;
  tokenHealthy?: boolean;
  needsReconnect?: boolean;
  needsReconnectSoon?: boolean;
} | null | undefined): boolean {
  return quickBooksTimeNeedsReconnect(status) || quickBooksTimeNeedsReconnectSoon(status);
}

export function formatQuickBooksTimeExpiry(expiresAt: string | null | undefined): string | null {
  if (expiresAt == null || expiresAt.trim() === "") {
    return null;
  }
  const parsed = new Date(expiresAt);
  if (!Number.isFinite(parsed.getTime())) {
    return null;
  }
  return parsed.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function quickBooksTimeBannerMessage(
  status: {
    connected?: boolean;
    tokenHealthy?: boolean;
    needsReconnectSoon?: boolean;
    expiresAt?: string | null;
  } | null | undefined,
  statusUnavailable: boolean,
): string {
  if (statusUnavailable) {
    return "QuickBooks Time connection status could not be loaded. Reconnect below; if that fails, the hub may still be waiting on a deploy with the QuickBooks Time sign-in routes.";
  }
  if (status?.connected && status.tokenHealthy === false) {
    return "QuickBooks Time sign-in expired: clock in, clock out, and break overlays are hidden until you reconnect.";
  }
  if (status?.connected && status.needsReconnectSoon === true) {
    const expiryLabel = formatQuickBooksTimeExpiry(status.expiresAt);
    return expiryLabel == null
      ? "QuickBooks Time sign-in expires soon. Renew now to keep clock overlays uninterrupted; the current sign-in keeps working while you do."
      : `QuickBooks Time sign-in expires ${expiryLabel}. Renew now to keep clock overlays uninterrupted; the current sign-in keeps working while you do.`;
  }
  return "QuickBooks Time is not connected: daily task clock in, clock out, and break overlays are hidden until you connect.";
}

export function friendlyQuickBooksTimeLoadError(
  raw: string | null | undefined,
  operatorEligible: boolean,
): string {
  const message = raw?.trim() ?? "";
  const normalized = message.toLowerCase();

  if (
    normalized.includes("refresh_token is invalid") ||
    normalized.includes("stop trying")
  ) {
    return operatorEligible
      ? "QuickBooks Time sign-in expired. Use Connect QuickBooks Time at the top of the page to sign in again. Station labor below still loads; clock and break overlays return after you reconnect."
      : "QuickBooks Time sign-in expired. Station labor below still loads; clock and break overlays are hidden until the hub operator reconnects QuickBooks Time.";
  }

  if (normalized.includes("token request failed")) {
    return operatorEligible
      ? "QuickBooks Time could not be reached. Try Connect QuickBooks Time at the top of the page. Station labor below still loads."
      : "QuickBooks Time could not be reached. Station labor below still loads; clock and break overlays are hidden until the hub operator reconnects.";
  }

  if (message === "") {
    return operatorEligible
      ? "QuickBooks Time overlays could not be loaded. Station labor below still loads."
      : "QuickBooks Time overlays could not be loaded. Station labor below still loads.";
  }

  return operatorEligible
    ? `QuickBooks Time overlays could not be loaded (${message}). Station labor below still loads.`
    : `QuickBooks Time overlays could not be loaded. Station labor below still loads.`;
}

export function friendlyDailyTaskLoadError(
  error: unknown,
  operatorEligible: boolean,
): string {
  if (error instanceof Error) {
    const normalized = error.message.toLowerCase();
    if (normalized.includes("refresh_token is invalid") || normalized.includes("quickbooks time token")) {
      return friendlyQuickBooksTimeLoadError(error.message, operatorEligible);
    }
  }
  return operatorEligible
    ? "The daily task report could not be loaded. If this mentions QuickBooks Time, use Connect QuickBooks Time at the top of the page and try again."
    : "The daily task report could not be loaded. Ask the hub operator to reconnect QuickBooks Time if clock overlays are missing.";
}
